import * as fs from 'fs';
import * as path from 'path';

import { makeNamespacedLog } from '@sb/log';
import { loadHostIdentity } from '@sb/service-interfaces/host/identity';

import type { Expression } from '../../types';

import type { CaptureConfig } from './captureConfig';
import type { CaptureStorageReport } from './captureStoragePressure';
import {
  captureStorageReporter,
  classifyCaptureStorage,
} from './captureStoragePressure';
import type {
  CaptureMetadata,
  ImageValidationFailure,
  StorageEquipment,
} from './captureUtils';
import {
  captureKindDirectory,
  convertImageToPng,
  formatCaptureTimestamp,
  generateCaptureFilename,
  metadataPathFor,
  serialiseRecordToTempFile,
  uploadToStorage,
  validateImage,
} from './captureUtils';
import type { LocalCaptureSettings } from './localCaptureSettings';
import {
  captureDirectoryFor,
  loadLocalCaptureSettings,
} from './localCaptureSettings';
import { pruneCaptureDirectory } from './pruneCaptureDirectory';
import { writeCaptureLocally } from './writeCaptureLocally';

const log = makeNamespacedLog('captureFrameToStorage');

/**
 * The two variables a capture publishes to the routine.
 *
 * Named so a step's `Variables` schema can be checked against them: a step
 * accepting a `captureConfig` has to be able to hold both, and the compiler
 * says so at the call site instead of at run time.
 *
 * `latestSaveFilepath` is RELATIVE, and has always been. It holds whatever the
 * step's `saveFilepath` expression resolved to — or the generated filename when
 * there was none — and every target resolves that against its own base:
 * `FTPServer.uploadFile` joins its `basePath`, `S3Bucket` prefixes its
 * `keyPrefix`, and a local target joins its configured directory. So no single
 * absolute location exists to publish, even with one target configured, and a
 * routine that treats this as an on-disk path is wrong about a single-target
 * capture too. The picker calls it a filename for that reason.
 */
export type CaptureVariableName = 'captureTimestamp' | 'latestSaveFilepath';

export interface CaptureFrameArgs {
  image: ArrayBuffer;
  captureConfig: CaptureConfig;

  /** Distinguishes the record's producer; also picks the log prefix. */
  stepKind: CaptureMetadata['stepKind'];

  /** The step's own arguments, minus `captureConfig`, for the record. */
  stepParameters: Record<string, unknown>;

  robotId: string;

  /** Which saved skill produced the results, for the skill-backed steps. */
  skillId?: string;

  /**
   * The routine this step belongs to, which with `skillId` picks the local
   * directory the frame is written to and bounded in.
   *
   * The OWNING routine, so a step inside a subroutine is attributed to the
   * subroutine rather than to whatever called it. That is the routine an
   * operator edits the step in, and so the one they would look under.
   */
  routineId?: string;

  /** The timestamp in the frame's name. Defaults to now. */
  captureTimestamp?: string;

  /**
   * The frame's name without its extension, for both copies. A `saveFilepath`
   * still names the external one.
   */
  fileName?: string;

  /** The named values in `fileName`, for the record. */
  nameValues?: Record<string, string>;

  /**
   * The results half of the record: a Locate record carries its poses (with
   * projected grasps) and camera, an Inspect record carries counts. They are
   * passed as one object so a new step kind adds a field here instead of
   * another parameter every existing caller has to thread through.
   */
  results?: Pick<
    CaptureMetadata,
    'locateResults' | 'camera' | 'inspectResults'
  >;

  evaluateExpression: (expression: Expression) => Promise<unknown>;
  getDeviceById: (id: string) => StorageEquipment | undefined;

  /**
   * Publishes a capture variable immediately.
   *
   * A callback rather than a returned pair, because the ORDER matters and the
   * caller cannot reproduce it. `captureTimestamp` has to reach the routine
   * before `saveFilepath` is evaluated, since that expression is allowed to
   * reference it to build the filename — so returning both at the end would
   * evaluate the path against a timestamp the routine has not been given yet.
   * It also has to survive the throwing validations below, which a step today
   * observes as a published timestamp with no saved path.
   */
  publishVariable: (name: CaptureVariableName, value: string) => void;

  /**
   * Tells an operator that this skill's capture directory is filling up.
   *
   * Optional, and never awaited: a robot with no way to reach the notification
   * service still inspects parts, and a capture that saved everything it was
   * asked to must not be reported as failed because a warning could not be
   * delivered.
   */
  reportStoragePressure?: (report: CaptureStorageReport) => void;
}

/**
 * Writes one frame to storage, with its record beside it when asked.
 *
 * Extracted from hand-written copies in the Locate and RunLocateSkill steps.
 * Behaviour is deliberately unchanged from those: the same validations throw in
 * the same order and with the same messages, `captureTimestamp` is published
 * before the path expression is evaluated, `latestSaveFilepath` is published
 * between the frame and the record, and an image that fails validation returns
 * the failure for the caller to hand to `fail` rather than throwing.
 *
 * Structured as encode-then-distribute: the frame is encoded and the record
 * serialised ONCE, then handed to each target in turn. With the single target
 * every routine configures today, that is the same work in the same order as
 * before. It is written this way because the Data plane adds a second target
 * kind, and encoding per target would both redo the PNG compression for each
 * and give two targets two chances to hold different pixels for one record.
 *
 * Returns the failure when the image is unusable, or `null` when the frame was
 * written.
 */
export async function captureFrameToStorage(
  args: CaptureFrameArgs,
): Promise<ImageValidationFailure | null> {
  const {
    image,
    captureConfig,
    stepKind,
    stepParameters,
    robotId,
    skillId,
    routineId,
    fileName,
    nameValues,
    results,
    reportStoragePressure,
    evaluateExpression,
    getDeviceById,
    publishVariable,
  } = args;

  // Before the expression is evaluated, which may reference it.
  const captureTimestamp =
    args.captureTimestamp ?? formatCaptureTimestamp(new Date());

  publishVariable('captureTimestamp', captureTimestamp);

  // Both copies take this name, unless a `saveFilepath` names the external one.
  const frameName = fileName
    ? `${fileName}.png`
    : generateCaptureFilename({
        stepKind,
        skillId,
        timestamp: captureTimestamp,
      });

  const remotePath = await resolveRemotePath({
    saveFilepath: captureConfig.saveFilepath,
    frameName,
    evaluateExpression,
  });

  // An EMPTY `saveEquipmentId` means no equipment was chosen, which is a
  // different thing from choosing one that has since been deleted. Only the
  // latter is an error worth stopping the routine over, and it keeps its
  // message: a named-but-missing id is a typo or a removed device, and failing
  // quietly there would upload a defect record nowhere and report success.
  //
  // Empty is now allowed through because a capture no longer has to go to
  // equipment at all — an Inspect step defaults to writing locally and may have
  // no FTP or S3 device configured. If nothing else takes the write either,
  // `writeToTargets` is what fails, and it says so.
  const legacyEquipment = captureConfig.saveEquipmentId
    ? getDeviceById(captureConfig.saveEquipmentId)
    : undefined;

  if (captureConfig.saveEquipmentId && !legacyEquipment) {
    throw new Error(
      `Storage equipment not found: ${captureConfig.saveEquipmentId}`,
    );
  }

  const { targets, localSettings } = await resolveTargets({
    captureConfig,
    legacyEquipment,
    getDeviceById,
    stepKind,
    skillId,
    frameName,
  });

  const validationFailure = validateImage(image);

  if (validationFailure) {
    return validationFailure;
  }

  // ENCODED ONCE, outside the loop. The same bytes then go to every target: an
  // encode per target would redo the whole PNG compression for each, and two
  // encodes of one frame are two chances for the targets to hold different
  // pixels for the same record.
  const stagedFrame = await convertImageToPng(image);

  try {
    const wroteFrame = await writeToTargets({
      targets,
      localPath: stagedFrame,
      remotePath,
      what: 'frame',
      stepKind,
    });

    // After the frame and before the record, matching what a step observes
    // today: a published path whose record then failed to upload.
    publishVariable('latestSaveFilepath', remotePath);

    if (captureConfig.saveMetadata) {
      const identity = await loadHostIdentity();

      // SERIALISED ONCE, for the same reason as the frame. This is also why the
      // record carries no per-target field: one set of bytes has to be correct
      // everywhere it is written.
      const stagedRecord = await serialiseRecordToTempFile({
        imageFilename: path.basename(remotePath),
        botId: robotId,
        botAlias: identity?.alias,
        timestamp: new Date().toISOString(),
        stepKind,
        skillId,
        nameValues,
        ...results,
        stepParameters,
      });

      try {
        await writeToTargets({
          // Only where the frame landed. A record beside no frame describes an
          // image the reader cannot open.
          targets: wroteFrame,
          localPath: stagedRecord,
          remotePath: metadataPathFor(remotePath),
          what: 'record',
          stepKind,
        });
      } finally {
        await unlinkQuietly(stagedRecord);
      }
    }
  } finally {
    await unlinkQuietly(stagedFrame);
  }

  // AFTER both writes, and only once per capture. Pruning between the frame and
  // its record would measure a frame whose sidecar does not exist yet, so a
  // directory at its limit could delete the capture it is in the middle of
  // writing. Running it after also means the new capture is part of what gets
  // measured, which is deliberate: a full directory should shed its oldest
  // capture rather than refuse the new one, because refusing would fail a
  // routine for a housekeeping reason.
  //
  // Failure here is swallowed. The frame and record are already written, and a
  // directory slightly over budget is a better outcome than an inspection
  // reported as failed after it successfully saved everything it was asked to.
  if (localSettings) {
    const outcome = await pruneCaptureDirectory({
      directory: localSettings.directory,
      settings: localSettings,
    }).catch((error) => {
      log.warn('captureFrameToStorage', 'Could not prune the local directory', {
        directory: localSettings.directory,
        error,
      });

      return null;
    });

    if (outcome) {
      const level = classifyCaptureStorage({
        outcome,
        limitBytes: localSettings.maxTotalBytes,
      });

      // Edge-triggered. Pruning runs after every capture, so a directory
      // sitting above the warning line would otherwise raise one per frame.
      // Asked unconditionally, including for `null`, because that is what
      // re-arms a directory whose pressure has eased.
      const shouldSend = captureStorageReporter.shouldReport(
        localSettings.directory,
        level,
      );

      if (shouldSend && level !== null) {
        reportStoragePressure?.({
          directory: localSettings.directory,
          level,
          usedBytes: outcome.usedBytes,
          limitBytes: localSettings.maxTotalBytes,
          skillId,
          routineId,
        });
      }

      // The robot's disk rather than this directory. Raised separately because
      // it is a different message with a different remedy: the directory did
      // everything it could and the space is going somewhere else, so no amount
      // of rotating captures answers it.
      //
      // Carries no skill for the same reason. Naming one would send an operator
      // to a skill that is inside its budget.
      if (captureStorageReporter.shouldReportDiskFloor(outcome.isBelowFloor)) {
        reportStoragePressure?.({
          directory: localSettings.directory,
          level: 'diskFloor',
          usedBytes: outcome.freeBytes ?? 0,
          limitBytes: localSettings.minFreeBytes,
          routineId,
        });
      }
    }
  }

  // The ids as well as the path, which does not say which routine took it.
  log.info('captureFrameToStorage', 'Capture completed', {
    stepKind,
    remotePath,
    routineId,
    skillId,
  });

  return null;
}

/**
 * The path this capture writes to: the step's expression when it set one, or a
 * generated filename when it did not.
 *
 * The three validations apply ONLY to an expression the step actually
 * configured. They are what makes a present-but-malformed path a loud failure,
 * and they must not be reachable by the generated name — `captureFileName`
 * makes every part of a generated name safe, so there is nothing about it left
 * to validate.
 *
 * `saveFilepath` being absent is the auto case. An expression that evaluates to
 * an empty string is still an error, deliberately: generating a path over it
 * would show an operator's unfinished config as though it had been used.
 */
async function resolveRemotePath(args: {
  saveFilepath: CaptureConfig['saveFilepath'];
  frameName: string;
  evaluateExpression: (expression: Expression) => Promise<unknown>;
}): Promise<string> {
  const { saveFilepath, frameName, evaluateExpression } = args;

  if (!saveFilepath) {
    return frameName;
  }

  const remotePath = await evaluateExpression(saveFilepath);

  if (typeof remotePath !== 'string') {
    throw new Error(
      `Filepath expression must evaluate to a string, received ${typeof remotePath}`,
    );
  }

  if (!remotePath) {
    throw new Error('Filepath expression must evaluate to a non-empty string');
  }

  if (!remotePath.endsWith('.png')) {
    throw new Error(`Filepath must end with .png (Filepath: ${remotePath})`);
  }

  return remotePath;
}

/** One destination a frame and its record are written to. */
interface ResolvedTarget {
  /** Named in the log line and in the failure, so a partial write is legible. */
  describe: string;
  /**
   * Writes one staged file to this destination.
   *
   * A closure per target rather than a `kind` the loop switches on, because
   * what a write MEANS differs by destination: an equipment target issues a
   * device command, a local target renames into a directory. Recovery differs
   * with it, so each target owns its own write and a single fused command
   * cannot express either one properly.
   */
  write: (stagedPath: string, resolvedPath: string) => Promise<void>;
}

/**
 * Every destination this capture writes to.
 *
 * The legacy `saveEquipmentId` comes first and always, because every saved
 * routine has one and the editor still writes it — `destinations` is additive, so an
 * empty array is exactly today's behaviour.
 *
 * A local target's directory is read here rather than passed in, so the routine
 * cannot name it. See `CaptureTarget` for why that separation is the
 * containment boundary rather than a preference.
 */
async function resolveTargets(args: {
  captureConfig: CaptureConfig;
  /** Absent when the step names no equipment; see the caller. */
  legacyEquipment: StorageEquipment | undefined;
  getDeviceById: (id: string) => StorageEquipment | undefined;
  /** Together these pick the local directory; see `captureDirectoryFor`. */
  stepKind: CaptureMetadata['stepKind'];
  skillId?: string;
  /** The generated filename, which the local copy takes. */
  frameName: string;
}): Promise<{
  targets: ResolvedTarget[];
  localSettings?: LocalCaptureSettings;
}> {
  const {
    captureConfig,
    legacyEquipment,
    getDeviceById,
    stepKind,
    skillId,
    frameName,
  } = args;

  const equipmentTarget = (
    id: string,
    equipment: StorageEquipment,
  ): ResolvedTarget => ({
    describe: id,
    write: (stagedPath, resolvedPath) =>
      uploadToStorage({
        tempFilePath: stagedPath,
        remotePath: resolvedPath,
        storageEquipment: equipment,
      }),
  });

  const targets: ResolvedTarget[] = legacyEquipment
    ? [equipmentTarget(captureConfig.saveEquipmentId, legacyEquipment)]
    : [];

  /**
   * Absent for every routine saved before `destinations` existed, which is every
   * routine today.
   *
   * A step's `this.args` is the RAW stored configuration, not the parsed one:
   * `Step._constructStepInstance` passes zod-validated args to the constructor
   * and then immediately does `step.args = args` with the original JSON. So no
   * zod default reaches this code, whatever the schema declares — which is why
   * the field is `.optional()` rather than defaulted. Iterating it unguarded
   * threw on the first capture of every existing routine.
   */
  const configured = captureConfig.destinations ?? [];

  for (const target of configured) {
    if (target.kind !== 'equipment') {
      continue;
    }

    const equipment = getDeviceById(target.equipmentId);

    if (!equipment) {
      throw new Error(`Storage equipment not found: ${target.equipmentId}`);
    }

    targets.push(equipmentTarget(target.equipmentId, equipment));
  }

  // Once, outside the loop, and at most one entry however many local targets a
  // routine lists: one step resolves to one directory, so a second local target
  // would write the same file to the same place twice — and reading the
  // settings per iteration would re-read the config file for no gain.
  //
  // The whole settings object is handed back, not just the directory, because
  // the prune that runs after the capture needs the same bound this read. One
  // read per capture, and no chance of the write and the prune disagreeing
  // about the directory if the file changes mid-capture.
  let localSettings: LocalCaptureSettings | undefined;

  if (configured.some((target) => target.kind === 'local')) {
    const configuredSettings = await loadLocalCaptureSettings();

    // The configured value is the ROOT; what a capture writes into is one
    // kind-and-skill directory below it, with the size bound applying to that
    // directory alone. Resolved once, here, so the write and the prune below
    // cannot be handed different directories.
    const directory = captureDirectoryFor({
      root: configuredSettings.directory,
      kind: captureKindDirectory(stepKind),
      skillId,
    });

    localSettings = { ...configuredSettings, directory };

    // Named without `resolvedPath`, which may be the operator's `saveFilepath`.
    // Where a local copy goes is robot configuration.
    const recordName = metadataPathFor(frameName);

    targets.push({
      describe: `local:${directory}`,
      // `resolvedPath` is consulted for WHICH of the two files this is, and
      // for nothing else. The frame and its record go through one target in
      // turn, so a single name here would have the record overwrite the frame
      // it describes. The extension is the signal because it is what
      // `metadataPathFor` swaps.
      write: async (stagedPath, resolvedPath) => {
        await writeCaptureLocally({
          stagedPath,
          directory,
          filename: resolvedPath.endsWith('.png') ? frameName : recordName,
        });
      },
    });
  }

  return { targets, localSettings };
}

/**
 * Writes one staged file to every target, and hands back those that took it.
 *
 * Per target rather than one fused command, because the equipment framework is
 * command-per-device and what recovery means differs by target. Every target is
 * attempted even after one fails, and a failure is logged against the target
 * that produced it — a write that reached two of three destinations should say
 * which one is missing, rather than reporting a single opaque failure.
 *
 * Throws only when NOTHING took the file. With one target that is the same
 * behaviour as before: its failure is the only failure, so it propagates.
 */
async function writeToTargets(args: {
  targets: ResolvedTarget[];
  localPath: string;
  remotePath: string;
  what: 'frame' | 'record';
  stepKind: CaptureMetadata['stepKind'];
}): Promise<ResolvedTarget[]> {
  const { targets, localPath, remotePath, what, stepKind } = args;

  const wrote: ResolvedTarget[] = [];
  const failures: Array<{ target: ResolvedTarget; error: unknown }> = [];

  for (const target of targets) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await target.write(localPath, remotePath);

      wrote.push(target);
    } catch (error) {
      failures.push({ target, error });

      log.error('writeToTargets', `Failed to write ${what} to a target`, {
        stepKind,
        target: target.describe,
        remotePath,
        error,
      });
    }
  }

  if (wrote.length > 0) {
    return wrote;
  }

  // NOTHING WAS EVEN ATTEMPTED. Distinct from "every target failed", and it
  // reached the aggregate message below until this guard existed — reporting
  // "any of the 0 configured destinations" followed by an empty reason list,
  // which describes no problem an operator can act on.
  //
  // The state is reachable: `saveEquipmentId` may be empty (which means none
  // chosen) while `destinations` is empty or absent, so a capture is switched
  // on with nowhere to put the frame. Validation now rejects that before a
  // routine runs; this is the runtime backstop for a config saved before it
  // did.
  if (failures.length === 0) {
    throw new Error(
      `Cannot write the ${what}: this step has capture switched on with no destination. ` +
        'Choose storage equipment, or turn on saving to the robot.',
    );
  }

  // A single target rethrows its own error untouched, so a routine configured
  // the way every routine is configured today reports exactly the message it
  // reported before this became a loop. Wrapping unconditionally would have
  // quietly reworded an existing failure for no reader's benefit.
  if (failures.length === 1) {
    throw failures[0].error;
  }

  const reasons = failures
    .map(({ target, error }) => {
      const reason = error instanceof Error ? error.message : String(error);

      return `${target.describe}: ${reason}`;
    })
    .join('; ');

  throw new Error(
    `Failed to write the ${what} to any of the ${failures.length} configured destinations — ${reasons}`,
  );
}

/** Cleanup that must not mask the failure that brought us here. */
async function unlinkQuietly(filePath: string): Promise<void> {
  await fs.promises.unlink(filePath).catch((error) => {
    log.warn('captureFrameToStorage', 'Failed to delete a staged file', {
      filePath,
      error,
    });
  });
}
